import { fileHref } from '../lib/fileAccess'
import { useState, useEffect } from 'react'
import { fetchRows } from '../lib/api'
import { ColFilterDropdown } from '../components/DataTable'
import html2pdf from 'html2pdf.js'
import { useAuth } from '../hooks/useAuth'
import ExportMenu from '../components/ExportMenu'
import { escHtml } from '../lib/escHtml'
import { expiryStatus, expiryToday } from '../lib/expiry'

const getVal = v => (v && typeof v === 'object' && 'value' in v) ? (v.value ?? '') : (v ?? '')

// the shared expiry rule (src/lib/expiry.js): valid THROUGH the expiry date in ship's time, "Expires today" counts as expiring soon
const certStatus = (r, today) => { const s = expiryStatus(r['Expiry Date'], today); return { key: s.chip, label: s.label, tone: s.tone } }

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'valid', label: 'Valid' },
  { id: 'expiring', label: 'Expiring soon' },
  { id: 'expired', label: 'Expired' },
]

export default function CrewCertificates({ onNavigate, initialFilter }) {
  const { config } = useAuth()
  const [rows, setRows] = useState([])
  const [crew, setCrew] = useState([])
  const [loading, setLoading] = useState(true)
  const VALID_CHIPS = ['all','valid','expiring','expired']
  const [chip, setChip] = useState(VALID_CHIPS.includes(initialFilter) ? initialFilter : 'all')
  const [colFilters, setColFilters] = useState({})
  // a certificate clicked under an AI answer ("looked up"): the list opens searched for it (once)
  const [q, setQ] = useState(() => { try { const o = JSON.parse(localStorage.getItem('hc_open_q') || 'null'); if (o && o.page === 'crew_certs') { localStorage.removeItem('hc_open_q'); return String(o.q || '') } } catch (e) {} return '' })

  useEffect(() => { load() }, [])
  async function load() {
    setLoading(true)
    try {
      const [docs, crewRows] = await Promise.all([
        fetchRows('crew_docs'),
        fetchRows('crew').catch(() => []),
      ])
      setRows(docs)
      setCrew(crewRows)
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  const crewNames = crew.map(c => (getVal(c['First Name']) + ' ' + getVal(c['Last Name'])).trim()).filter(n => n.length > 1)

  function matchCrew(title) {
    const t = (title || '').toLowerCase()
    const hit = [...crewNames].sort((a, b) => b.length - a.length).find(n => t.includes(n.toLowerCase()))
    return hit || ''
  }

  function cleanCertName(title, crewName) {
    let s = title || ''
    if (crewName) {
      const re = new RegExp('^' + crewName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*[\u2013-]?\\s*', 'i')
      s = s.replace(re, '').trim()
    }
    return s || title || '-'
  }

  // Build flat rows with computed columns so ColFilterDropdown can read them directly
  const today = expiryToday(config?.timezone)
  const enriched = rows.map(r => {
    const title = getVal(r['Title'])
    const crewName = matchCrew(title)
    const st = certStatus(r, today)
    return {
      _orig: r,
      'Crew': crewName || '-',
      'Certificate': cleanCertName(title, crewName),
      'Type': getVal(r['Type']) || '-',
      'Expiry Date': getVal(r['Expiry Date']) || '-',
      'Status': st.label,
      _tone: st.tone,
      _key: st.key,
      _id: r.id,
      _file: getVal(r['File URL']) || '',
    }
  }).sort((a, b) => {
    const ta = Date.parse(a['Expiry Date']) || Infinity
    const tb = Date.parse(b['Expiry Date']) || Infinity
    return ta - tb
  })

  const ql = q.trim().toLowerCase()
  const shown = enriched.filter(e => {
    if (chip !== 'all' && e._key !== chip) return false
    if (!Object.entries(colFilters).every(([k, v]) => !v || e[k] === v)) return false
    if (ql) {
      const hay = (e['Crew'] + ' ' + e['Certificate'] + ' ' + e['Type'] + ' ' + getVal(e._orig['Title'])).toLowerCase()   // + the full title (a link from the chat searches by it)
      if (!hay.includes(ql)) return false
    }
    return true
  })

  const counts = {
    all: enriched.length,
    valid: enriched.filter(e => e._key === 'valid').length,
    expiring: enriched.filter(e => e._key === 'expiring').length,
    expired: enriched.filter(e => e._key === 'expired').length,
  }

  const activeColFilters = Object.values(colFilters).filter(Boolean).length
  const setCol = (key, val) => setColFilters(prev => ({ ...prev, [key]: val }))

  function exportCertPdf() {
    const NAVY='#0A1628', GOLD='#C9A84C', LINE='#E5E9F1', MUTE='#67748C', TEXT='#16233B'
    const fmtLong = (iso) => { const d=new Date(iso); return isNaN(d)?'-':d.toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) }
    const groups = [
      { key:'expired', label:'EXPIRED', col:'#C93B47' },
      { key:'expiring', label:'EXPIRING SOON (within 60 days)', col:'#8a5c0a' },
      { key:'valid', label:'VALID', col:'#137050' },
    ]
    let body=''
    groups.forEach(g => {
      const items = shown.filter(e => e._key === g.key)
      if(!items.length) return
      const rows = items.map((e,i) => `<tr style="border-bottom:1px solid ${LINE};">
        <td style="padding:5px 7px;">${(e['Crew']||'-').replace(/</g,'&lt;')}</td>
        <td style="padding:5px 7px;">${(e['Certificate']||'-').replace(/</g,'&lt;')}</td>
        <td style="padding:5px 7px;color:${MUTE};">${(e['Type']||'-').replace(/</g,'&lt;')}</td>
        <td style="padding:5px 7px;">${e['Expiry Date']==='-'?'-':fmtLong(e['Expiry Date'])}</td></tr>`).join('')
      body += `<div style="margin-bottom:20px;page-break-inside:avoid;">
        <div style="background:${g.col};color:#fff;padding:5px 10px;font-size:11px;font-weight:700;letter-spacing:.08em;margin-bottom:6px;">${g.label} &middot; ${items.length}</div>
        <table style="width:100%;border-collapse:collapse;font-size:10px;color:${TEXT};">
          <thead><tr style="background:#F3F5F9;color:${MUTE};text-align:left;">
            <th style="padding:5px 7px;width:150px;">Crew</th><th style="padding:5px 7px;">Certificate</th>
            <th style="padding:5px 7px;width:150px;">Type</th><th style="padding:5px 7px;width:110px;">Expiry</th>
          </tr></thead><tbody>${rows}</tbody></table></div>`
    })
    const vessel = (config && config.vessel_name) || 'Vessel'
    const today = new Date().toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})
    const html = `<div style="width:960px;padding:6px 10px;font-family:Inter,Helvetica,Arial,sans-serif;color:${TEXT};background:#fff;">
      <div style="display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid ${GOLD};padding-bottom:9px;">
        <div><div style="font-size:19px;font-weight:800;letter-spacing:.12em;color:${NAVY};">HEYCAPTAIN</div>
          <div style="font-size:9px;color:${MUTE};letter-spacing:.18em;text-transform:uppercase;">Crew Certificate Expiry Report</div></div>
        <div style="text-align:right;font-size:9.5px;color:${MUTE};line-height:1.7;">
          <div style="font-size:13px;font-weight:700;color:${TEXT};">${escHtml(vessel)}</div><div>${today}</div></div>
      </div>
      <div style="background:${NAVY};color:#fff;padding:6px 11px;font-size:10px;letter-spacing:.14em;text-transform:uppercase;margin:0 0 16px;">
        ${shown.filter(e=>e._key==='expired').length} expired &middot; ${shown.filter(e=>e._key==='expiring').length} expiring soon &middot; ${shown.filter(e=>e._key==='valid').length} valid</div>
      ${body || '<div style="color:'+MUTE+';font-size:11px;">No certificates on file.</div>'}
      <div style="margin-top:12px;padding-top:9px;border-top:1px solid ${LINE};display:flex;justify-content:space-between;font-size:8.5px;color:${MUTE};">
        <span style="font-weight:800;letter-spacing:.12em;color:${NAVY};">HEYCAPTAIN</span>
        <span>${escHtml(vessel)} &middot; Crew Certificate Expiry Report &middot; ${today}</span></div></div>`
    const el=document.createElement('div'); el.innerHTML=html; document.body.appendChild(el)
    const fname=('Crew_Cert_Report_'+vessel+'_'+new Date().toISOString().split('T')[0]).replace(/[^a-z0-9]+/gi,'_')+'.pdf'
    html2pdf().set({ margin:8, filename:fname, image:{type:'jpeg',quality:0.98}, html2canvas:{scale:2,useCORS:true,windowWidth:960,width:960}, pagebreak:{mode:['legacy']}, jsPDF:{unit:'mm',format:'a4',orientation:'portrait'} }).from(el).save().then(()=>document.body.removeChild(el))
  }

  const COLS = ['Crew', 'Certificate', 'Type', 'Expiry Date', 'Status', 'File']
  const FILTERABLE = ['Crew', 'Type', 'Status']

  return (
    <div style={{ padding: '0 4px' }}>
      <button onClick={() => onNavigate && onNavigate('crew')}
        style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, padding: '4px 0', marginBottom: 10 }}>
        &#8592; Back to Crew
      </button>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => setChip(f.id)}
            style={{
              fontSize: 12, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
              border: '0.5px solid ' + (chip === f.id ? 'var(--gold2)' : 'var(--border)'),
              background: chip === f.id ? 'rgba(201,147,58,.12)' : 'var(--hull)',
              color: chip === f.id ? 'var(--gold2)' : 'var(--muted)',
            }}>
            {f.label} <span style={{ opacity: .7 }}>{counts[f.id]}</span>
          </button>
        ))}
        {activeColFilters > 0 && (
          <button onClick={() => setColFilters({})} style={{ fontSize: 11, color: 'var(--gold2)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
            Clear filters ({activeColFilters})
          </button>
        )}
        <input className="search-inp" placeholder="Search certificates..." value={q} onChange={e => setQ(e.target.value)} style={{ marginLeft: 'auto' }} />
        <button onClick={exportCertPdf} style={{ border: '0.5px solid var(--gold2)', background: 'rgba(201,147,58,.12)', color: 'var(--gold2)', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 7, cursor: 'pointer', whiteSpace: 'nowrap' }}>Export PDF</button>
        <ExportMenu className="" style={{ ...EXPORT_BTN, fontSize: 12 }} get={() => ({ title: 'Crew certificates', rows: shown, columns: COLS.filter(c => c !== 'File').map(c => ({ key: c, label: c })) })} />
      </div>

      {loading ? (
        <div style={{ color: 'var(--muted)', fontSize: 13, padding: 20 }}>Loading certificates...</div>
      ) : shown.length === 0 ? (
        <div style={{ color: 'var(--muted)', fontSize: 13, padding: 20 }}>No certificates match.</div>
      ) : (
        <div style={{ border: '0.5px solid var(--border)', borderRadius: 12, overflow: 'visible' }}>
          <div style={cs.headRow}>
            {COLS.map(c => (
              <span key={c} style={{ display: 'inline-flex', alignItems: 'center' }}>
                {c === 'File' ? '' : c.toUpperCase()}
                {FILTERABLE.includes(c) && (
                  <ColFilterDropdown colKey={c} rows={enriched} value={colFilters[c] || ''} onChange={val => setCol(c, val)} />
                )}
              </span>
            ))}
          </div>
          {shown.map((e, i) => (
            <div key={e._id || i} style={{ ...cs.row, background: e._key === 'expired' ? 'rgba(192,67,74,.06)' : 'transparent' }}>
              <span style={{ fontSize: 12.5, color: 'var(--text)', fontWeight: 500 }}>{e['Crew'] === '-' ? '\u2014' : e['Crew']}</span>
              <span style={{ fontSize: 12.5, color: 'var(--text)' }}>{e['Certificate']}</span>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{e['Type']}</span>
              <span style={{ fontSize: 12, color: 'var(--text)' }}>{e['Expiry Date']}</span>
              <span><span className={'pill pill-' + e._tone}>{e['Status']}</span></span>
              <span>{e._file
                ? <a href={fileHref(e._file)} target="_blank" rel="noreferrer" title="Open certificate" style={{ textDecoration: 'none', fontSize: 15 }}>&#128206;</a>
                : <span style={{ opacity: .25, fontSize: 15 }} title="No file">&#128206;</span>}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const GRID = '1.2fr 2fr 1.1fr 1fr 1fr 0.5fr'
const cs = {
  headRow: { display: 'grid', gridTemplateColumns: GRID, gap: 0, padding: '10px 16px', background: 'var(--hull)', borderBottom: '0.5px solid var(--border)', fontSize: 10, color: 'var(--muted)', letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 600 },
  row: { display: 'grid', gridTemplateColumns: GRID, gap: 0, padding: '11px 16px', borderBottom: '0.5px solid var(--border)', alignItems: 'center' },
}
const EXPORT_BTN = { border: '0.5px solid var(--gold2)', background: 'rgba(201,147,58,.12)', color: 'var(--gold2)', fontWeight: 600, padding: '6px 14px', borderRadius: 7, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' }
